"use client";

import { getClientFunctions } from "@/lib/firebase-client";
import { httpsCallable } from "firebase/functions";

export type CreateCheckoutSessionInput = {
  tier: "full_pro";
  successUrl?: string;
  cancelUrl?: string;
};

export type CreateCheckoutSessionResult = {
  sessionId: string;
  url: string;
};

export async function createCheckoutSession(
  input: CreateCheckoutSessionInput,
): Promise<CreateCheckoutSessionResult> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<CreateCheckoutSessionInput, CreateCheckoutSessionResult>(
    functions,
    "createCheckoutSession",
  );

  const response = await callable(input);
  return response.data;
}

export type CancelSubscriptionInput = {
  cancelImmediately?: boolean;
};

export async function cancelSubscription(input: CancelSubscriptionInput = {}): Promise<void> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<CancelSubscriptionInput, { success?: boolean }>(
    functions,
    "cancelSubscription",
  );

  await callable(input);
}

export type BillingHistoryItem = {
  id: string;
  date: number;
  amount: number;
  currency: string;
  status: string;
  description: string;
  invoiceUrl?: string;
  receiptUrl?: string;
};

export type GetBillingHistoryResult = {
  items: BillingHistoryItem[];
  hasMore: boolean;
};

export async function getBillingHistory(limit: number = 10): Promise<GetBillingHistoryResult> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }

  const callable = httpsCallable<{ limit?: number }, GetBillingHistoryResult>(
    functions,
    "getBillingHistory",
  );

  const response = await callable({ limit });
  return response.data;
}

